// pending-0.1.19 bump for CD AcctInq dual-call port
/**
 * CdInqService — dual-call AcctInq port from legacy SPFx.
 *
 * Source of truth:
 *   `C:\Projects\retail-account-opening\spfx-account-opening-v2\
 *    pcb-account-opening-v2\src\webparts\accountOpeningV2\components\
 *    screens\CdMaintenanceScreen.tsx:144-196`
 *
 * Why two calls:
 *   jXchange (Core Director) filters the AcctInq response to the FIRST
 *   include element only. `IncXtendElemArray.IncXtendElemInfo.XtendElem`
 *   is a scalar in practice — if you ask for both extensions, only
 *   `x_TimeDepInfoRec` comes back populated. Confirmed by JHA case
 *   16782206 and reproduced against PCB Core Director 2025-06-11.
 *
 *   So we fire 2 calls in parallel:
 *     Call 1 → `x_TimeDepInfoRec`      → maturity, term, open date, penalty amt, renewal
 *     Call 2 → `x_TimeDepRateIntInfo`  → current rate, interest disp, next interest date
 *
 * Route:
 *   `POST /api/jxrl/Deposit/AcctInq` via `ApiClient.jxrl.post('Deposit/AcctInq', ...)`
 *   (proxy route `jxrl/{*operation}` is `JxChangeProxyFunction.cs:22`).
 *
 * No fixtures. No hardcoded penalty / rate / maturity values. If Core
 * doesn't return a field, we return `null` and let the UI render "—".
 */
import type { ApiClient } from '@retail-ops/api-client';
import type { RawJxResponse } from '@retail-ops/shared-types';
import type { CdDetail } from '../types';

// Account type 30 = CD in Core Director (also used for IRA certs — same payload shape).
const CD_ACCT_TYPE = '30';

const INTEREST_DISP_LABELS: Record<string, string> = {
  B: 'Add to balance (capitalize)',
  I: 'Deposit to account',
  A: 'Pay by ACH',
  C: 'Pay by check',
};

/** `#text: null` is how jXRL serializes missing string values. Drain to ''. */
function asText(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (v && typeof v === 'object') {
    const maybeText = (v as Record<string, unknown>)['#text'];
    if (maybeText == null) return '';
    return String(maybeText);
  }
  return v == null ? '' : String(v);
}

/** Parse a decimal string / number from jXRL; null if not a finite number. */
function asNumber(v: unknown): number | null {
  const s = asText(v).trim();
  if (!s) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize a Core Director date to ISO YYYY-MM-DD.
 * Core returns either 8-char `YYYYMMDD`, already-ISO `YYYY-MM-DD`, or
 * `0001-01-01` (sentinel = "no value"). Returns null for empties.
 */
function asIsoDate(v: unknown): string | null {
  const raw = asText(v).trim();
  if (!raw) return null;
  if (raw === '0001-01-01' || raw === '00010101') return null;
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  // Try Date parse as a last resort.
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

/** Days between today (local midnight) and an ISO YYYY-MM-DD date. */
function dayDiff(iso: string, now: Date = new Date()): number {
  const target = new Date(`${iso}T00:00:00`).getTime();
  const today = new Date(`${now.toISOString().slice(0, 10)}T00:00:00`).getTime();
  return Math.round((target - today) / 86_400_000);
}

function getObj(src: unknown, key: string): Record<string, unknown> {
  if (!src || typeof src !== 'object') return {};
  const v = (src as Record<string, unknown>)[key];
  if (!v || typeof v !== 'object') return {};
  // jXRL's `{#text: null}` shape means "missing" — flatten to empty.
  const maybeText = (v as Record<string, unknown>)['#text'];
  if (Object.keys(v as Record<string, unknown>).length === 1 && maybeText === null) return {};
  return v as Record<string, unknown>;
}

/**
 * Dig through the jXRL AcctInq envelope to find `TimeDepAcctInqRec`.
 * Core Director wraps the payload under `AcctInqRs_MType.AcctRec_CType`
 * in some driver versions and directly on the root in others (observed
 * in production probes — see api-wiring digest §2.2 Q7). We handle both.
 */
function findTimeDepRec(resp: RawJxResponse | Record<string, unknown>): Record<string, unknown> {
  const root = resp as Record<string, unknown>;
  // Prefer the explicitly wrapped path.
  const mType = getObj(root, 'AcctInqRs_MType');
  const acctRec = Object.keys(mType).length ? getObj(mType, 'AcctRec_CType') : {};
  if (Object.keys(acctRec).length && acctRec['TimeDepAcctInqRec']) {
    return getObj(acctRec, 'TimeDepAcctInqRec');
  }
  // Fall back to the flat shape (what legacy SPFx saw in prod).
  if (root['TimeDepAcctInqRec']) {
    return getObj(root, 'TimeDepAcctInqRec');
  }
  // Sometimes Core returns a plain `AcctRec` without the M-type wrapper.
  if (root['AcctRec_CType']) {
    return getObj(getObj(root, 'AcctRec_CType'), 'TimeDepAcctInqRec');
  }
  return {};
}

/** Derive `{autoRenew, renewalCode}` from Core Director `RenCode`. */
function decodeRenCode(raw: string): { autoRenew: boolean | null; renewalCode: string | null } {
  const code = raw.trim().toUpperCase();
  if (!code) return { autoRenew: null, renewalCode: null };
  if (code === 'N' || code === 'NONE' || code === 'NO') {
    return { autoRenew: false, renewalCode: code };
  }
  if (code === 'Y' || code === 'YES' || code === 'A' || code === 'AUTO') {
    return { autoRenew: true, renewalCode: code };
  }
  return { autoRenew: null, renewalCode: code };
}

/** Build the jXRL AcctInq request payload for a given extension element. */
function buildAcctInqBody(acctId: string, xtendElem: 'x_TimeDepInfoRec' | 'x_TimeDepRateIntInfo'): Record<string, unknown> {
  return {
    InAcctId: { AcctId: acctId, AcctType: CD_ACCT_TYPE },
    IncAcctInfo: true,
    IncXtendElemArray: {
      IncXtendElemInfo: { XtendElem: xtendElem },
    },
  };
}

export interface FetchCdDetailOptions {
  signal?: AbortSignal;
}

export class CdInqService {
  public constructor(private readonly client: ApiClient) {}

  /**
   * Dual-call AcctInq + merge → CdDetail.
   *
   * Per legacy SPFx pattern, both calls are kicked off in parallel
   * (`Promise.all`). If one fails we still try to hydrate whatever the
   * other call returned — partial data is better than no data for
   * officers who just want to see the balance / maturity date.
   */
  public async fetchCdDetail(acctId: string, opts: FetchCdDetailOptions = {}): Promise<CdDetail> {
    if (!acctId) throw new Error('acctId is required');

    const { signal } = opts;
    const postOpts = signal ? { signal } : undefined;

    const call1 = this.client.jxrl.post<Record<string, unknown>, RawJxResponse>(
      'Deposit/AcctInq',
      buildAcctInqBody(acctId, 'x_TimeDepInfoRec'),
      postOpts,
    );
    const call2 = this.client.jxrl.post<Record<string, unknown>, RawJxResponse>(
      'Deposit/AcctInq',
      buildAcctInqBody(acctId, 'x_TimeDepRateIntInfo'),
      postOpts,
    );

    const [r1, r2] = await Promise.all([call1, call2]);
    return CdInqService.merge(acctId, r1, r2);
  }

  /**
   * Merge the two AcctInq responses into a single `CdDetail`.
   *
   * Exposed as a static for testability — feed it canned JSON fixtures
   * from jXchange probes and assert on the mapped shape.
   */
  public static merge(acctId: string, resp1: RawJxResponse, resp2: RawJxResponse): CdDetail {
    // --------------------------------------------------------------------
    // Call 1 — x_TimeDepInfoRec (maturity, term, penalty, renewal, balance)
    // --------------------------------------------------------------------
    const td1 = findTimeDepRec(resp1);
    const xTd = getObj(td1, 'x_TimeDepInfoRec');
    const timeDepObj = getObj(xTd, 'TimeDepInfoRec');

    // --------------------------------------------------------------------
    // Call 2 — x_TimeDepRateIntInfo (rate, interest disp, next int date)
    // --------------------------------------------------------------------
    const td2 = findTimeDepRec(resp2);
    const xRate = getObj(td2, 'x_TimeDepRateIntInfo');
    const rateInfo = getObj(xRate, 'TimeDepRateIntInfo');

    // --------------------------------------------------------------------
    // Field extraction — every value is nullable; we NEVER synthesize a
    // fallback. If Core didn't send it, UI renders "—".
    // --------------------------------------------------------------------
    const balance = asNumber(xTd['CurBal']) ?? asNumber(td1['CurBal']);
    const accruedInterest = asNumber(xTd['AccrInt']) ?? asNumber(td1['AccrInt']);
    const penaltyAmount = asNumber(xTd['PenAmt']);
    const productDescription = asText(xTd['ProdDesc']) || asText(timeDepObj['ProdDesc']) || null;
    const statusDescription = asText(xTd['AcctStatDesc']) || asText(timeDepObj['AcctStat']) || null;
    const cifId = asText(td1['CustId']) || asText(timeDepObj['CustId']) || null;
    const branchCode = asText(td1['BranchCode']) || asText(timeDepObj['BranchCode']) || null;

    const maturityDate = asIsoDate(timeDepObj['MatDt']);
    const openedDate = asIsoDate(timeDepObj['OpenDt']);
    const productCode = asText(timeDepObj['ProdCode']) || null;

    // CDTerm arrives as a string like "12" (months). Parse cautiously —
    // some products use units other than months (CDTermUnits); we keep
    // only numeric months and surface the raw string via productCode if
    // the caller needs the units.
    const cdTerm = asText(timeDepObj['CDTerm']).trim();
    const termMonthsParsed = cdTerm ? Number.parseInt(cdTerm, 10) : NaN;
    const termMonths = Number.isFinite(termMonthsParsed) ? termMonthsParsed : null;

    // Renewal — RenCode + renewal count + last renewal date.
    const { autoRenew, renewalCode } = decodeRenCode(asText(timeDepObj['RenCode']));
    const renewalCount = asNumber(timeDepObj['RenCnt']) ?? asNumber(timeDepObj['RenewalCount']);
    const lastRenewalDate =
      asIsoDate(timeDepObj['LastRenewDt']) ?? asIsoDate(timeDepObj['LastRenewalDate']);

    // Penalty formula — opaque code, multiple possible field names seen.
    const penaltyFormulaCode =
      asText(timeDepObj['PenMethodCode']) ||
      asText(timeDepObj['PenCode']) ||
      asText(xTd['PenMethodCode']) ||
      null;

    // --------------------------------------------------------------------
    // Rate / interest — from call 2.
    // --------------------------------------------------------------------
    const currentRate = asNumber(rateInfo['IntRate']) ?? asNumber(rateInfo['CurrentRate']);
    const nextInterestDate =
      asIsoDate(rateInfo['NextIntDt']) ??
      asIsoDate(rateInfo['NextInterestDt']) ??
      asIsoDate(rateInfo['NextIntDate']);

    const intDispCode = asText(rateInfo['IntDisp']).trim().toUpperCase() || null;
    const interestDisposition = intDispCode ? (INTEREST_DISP_LABELS[intDispCode] ?? intDispCode) : null;

    // --------------------------------------------------------------------
    // Derived — days to maturity + grace (±10d per CdMaintenanceScreen:368).
    // --------------------------------------------------------------------
    let daysToMaturity: number | null = null;
    let graceActive = false;
    let graceDaysRemaining: number | null = null;
    if (maturityDate) {
      daysToMaturity = dayDiff(maturityDate);
      if (daysToMaturity >= -10 && daysToMaturity <= 10) {
        graceActive = true;
        // Grace runs 10 days AFTER maturity per PCB ops policy; before
        // maturity the CD isn't yet in grace, it's pre-maturity.
        graceDaysRemaining = daysToMaturity >= 0 ? 10 - daysToMaturity : 0;
      }
    }

    return {
      acctId,
      cifId,
      balance,
      accruedInterest,
      openedDate,
      maturityDate,
      termMonths,
      currentRate,
      nextInterestDate,
      autoRenew,
      renewalCode,
      penaltyAmount,
      penaltyFormulaCode,
      interestDisposition,
      interestDispositionCode: intDispCode,
      productCode,
      productDescription,
      branchCode,
      statusDescription,
      renewalCount,
      lastRenewalDate,
      daysToMaturity,
      graceActive,
      graceDaysRemaining,
    };
  }
}

/** Factory for callers that only have the ApiClient (no DI container). */
export function createCdInqService(client: ApiClient): CdInqService {
  return new CdInqService(client);
}
